// v2.19.35 — Page tracking client `/track/:order_ref` — PUBLIQUE (pas d'auth).
// Polling 8s pour récupérer le statut + courier_lat/lng en temps réel.
// Si Mapbox token dispo → carte minimaliste avec pins pickup/drop/courier.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const STATUS_FLOW = [
  { k: "accepted",    l: "Livreur en route pickup", icon: "🚖" },
  { k: "picked_up",   l: "Colis pris en charge",     icon: "📦" },
  { k: "delivering",  l: "En livraison",             icon: "🛵" },
  { k: "delivered",   l: "Livré",                    icon: "✅" },
];

export default function TrackOrderPage() {
  const { order_ref } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/v3/delivery/track/${encodeURIComponent(order_ref)}`);
      const d = await r.json();
      if (r.ok && d.ok) { setData(d); setError(null); }
      else setError(d.error || `HTTP ${r.status}`);
    } catch (_) { setError("network_error"); }
  }, [order_ref]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Init Mapbox une fois data dispo.
  useEffect(() => {
    if (!data || !mapEl.current || mapRef.current || !MAPBOX_TOKEN) return;
    (async () => {
      try {
        const mod = await import("mapbox-gl");
        const mapboxgl = mod.default;
        await import("mapbox-gl/dist/mapbox-gl.css");
        mapboxgl.accessToken = MAPBOX_TOKEN;
        const o = data.order;
        const map = new mapboxgl.Map({
          container: mapEl.current,
          style: "mapbox://styles/mapbox/standard",
          center: [o.pickup.lng, o.pickup.lat],
          zoom: 12,
          pitch: 45,
          maxZoom: 20,
        });
        mapRef.current = map;
        // v2.19.35a — fallback si Standard Satellite échoue (token sans v3).
        map.on("error", (e) => {
          if (e && e.error && /style/i.test(String(e.error.message || ""))) {
            try { map.setStyle("mapbox://styles/mapbox/satellite-streets-v12"); } catch (_) {}
          }
        });
        map.on("load", () => {
          // v2.19.35b — Standard v3 PUR (pas satellite) : dusk + landmarks 3D.
          try {
            map.setConfigProperty("basemap", "lightPreset", "dusk");
            map.setConfigProperty("basemap", "show3dObjects", true);
            map.setConfigProperty("basemap", "showRoadLabels", true);
            map.setConfigProperty("basemap", "showPlaceLabels", true);
          } catch (_) {}
          // Pickup
          const pickEl = document.createElement("div");
          pickEl.innerHTML = "📦";
          pickEl.style.fontSize = "26px";
          markersRef.current.pickup = new mapboxgl.Marker({ element: pickEl }).setLngLat([o.pickup.lng, o.pickup.lat]).addTo(map);
          // Drop
          const dropEl = document.createElement("div");
          dropEl.innerHTML = "📍";
          dropEl.style.fontSize = "26px";
          markersRef.current.drop = new mapboxgl.Marker({ element: dropEl }).setLngLat([o.drop.lng, o.drop.lat]).addTo(map);
          // Ligne pickup→drop
          map.addSource("route", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [[o.pickup.lng, o.pickup.lat], [o.drop.lng, o.drop.lat]] } } });
          map.addLayer({ id: "route-line", type: "line", source: "route", paint: { "line-color": "#FBBF24", "line-width": 3, "line-dasharray": [2, 2] } });
          // Fit bounds
          const bounds = new mapboxgl.LngLatBounds().extend([o.pickup.lng, o.pickup.lat]).extend([o.drop.lng, o.drop.lat]);
          map.fitBounds(bounds, { padding: 60, duration: 0 });
        });
      } catch (e) { console.warn("Mapbox load error:", e); }
    })();
    return () => {
      try { mapRef.current?.remove(); mapRef.current = null; } catch (_) {}
    };
  }, [data]);

  // Update courier marker on poll.
  useEffect(() => {
    if (!data || !mapRef.current || !MAPBOX_TOKEN) return;
    const { courier_lat, courier_lng } = data.order;
    if (!Number.isFinite(courier_lat) || !Number.isFinite(courier_lng)) return;
    (async () => {
      const mod = await import("mapbox-gl");
      const mapboxgl = mod.default;
      if (markersRef.current.courier) {
        markersRef.current.courier.setLngLat([courier_lng, courier_lat]);
      } else {
        const el = document.createElement("div");
        el.innerHTML = "🛵";
        el.style.fontSize = "30px";
        markersRef.current.courier = new mapboxgl.Marker({ element: el }).setLngLat([courier_lng, courier_lat]).addTo(mapRef.current);
      }
    })();
  }, [data]);

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ color: "#F87171", fontSize: 22 }}>Commande introuvable</h1>
          <p style={{ color: "#94A3B8", marginTop: 12, fontSize: 13 }}>Référence inconnue ou expirée. Vérifie le lien.</p>
          <a href="/" style={ctaAltStyle}>← Retour AYA AI</a>
        </div>
      </div>
    );
  }
  if (!data) return <div style={pageStyle}><div style={{ color: "#94A3B8", padding: 24 }}>Chargement…</div></div>;

  const o = data.order;
  const currentStepIdx = STATUS_FLOW.findIndex((s) => s.k === o.status);

  return (
    <div style={pageStyle} data-testid="track-order-page">
      <header style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#FBBF24" }}>🇨🇮 AYA Livraison</div>
        <div data-testid="track-order-ref" style={{ fontFamily: "monospace", fontSize: 12, color: "#93C5FD", marginTop: 2 }}>{o.order_ref}</div>
      </header>

      <div style={{ padding: 16 }}>
        <div data-testid="track-status-badge" style={statusBadgeBig(o.status)}>
          {(STATUS_LABELS[o.status] || { l: o.status, icon: "•" }).icon} {(STATUS_LABELS[o.status] || { l: o.status }).l}
        </div>

        {/* Progress bar 4 steps */}
        {currentStepIdx >= 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 14 }}>
            {STATUS_FLOW.map((s, i) => (
              <div key={s.k} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= currentStepIdx ? "#22C55E" : "rgba(255,255,255,0.10)" }} />
            ))}
          </div>
        )}

        {data.eta_min && (
          <div data-testid="track-eta" style={{ marginTop: 14, fontSize: 14, color: "#FBBF24", fontWeight: 700, textAlign: "center" }}>
            ETA ~{data.eta_min} min
          </div>
        )}

        {MAPBOX_TOKEN && (
          <div ref={mapEl} data-testid="track-map" style={{ width: "100%", height: 320, marginTop: 14, borderRadius: 12, overflow: "hidden", background: "#1F2937" }} />
        )}

        <div style={{ ...cardStyle, marginTop: 14 }}>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>📦 Pickup</div>
          <div style={{ fontSize: 14, color: "#E8ECF7", marginTop: 2 }}>{o.commerce_name}</div>
          {o.pickup?.description && <div style={{ fontSize: 12, color: "#94A3B8" }}>{o.pickup.description}</div>}
        </div>

        <div style={{ ...cardStyle, marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>📍 Drop</div>
          <div style={{ fontSize: 14, color: "#E8ECF7", marginTop: 2 }}>{o.drop?.description || o.drop?.address_civ || "—"}</div>
          {o.client_name_masked && <div style={{ fontSize: 12, color: "#94A3B8" }}>Pour : {o.client_name_masked}</div>}
        </div>

        <div style={{ ...cardStyle, marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>Détails</div>
          <div style={{ fontSize: 13, color: "#E8ECF7", marginTop: 4 }}>{o.items_summary}</div>
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>📏 {o.distance_km} km · 💰 {o.delivery_fee_fcfa} FCFA</div>
        </div>

        {o.status === "delivered" && (
          <Link to={`/replay/${o.order_ref}`} data-testid="track-replay-btn" style={replayBtnStyle}>
            🎬 Voir le replay
          </Link>
        )}
        {o.status === "delivered" && !o.ratings?.client_rating_courier && <RateForm orderRef={o.order_ref} />}

        <div style={{ marginTop: 18, textAlign: "center" }}>
          <a href={`https://wa.me/?text=${encodeURIComponent(`Suivi livraison AYA : ${window.location.href}`)}`} target="_blank" rel="noopener noreferrer" data-testid="track-share-btn" style={shareBtnStyle}>
            📤 Partager le suivi
          </a>
        </div>

        <div style={{ textAlign: "center", marginTop: 18, color: "#64748b", fontSize: 11 }}>Livraison via AYA AI 🇨🇮</div>
      </div>
    </div>
  );
}

function RateForm({ orderRef }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!rating) return;
    await fetch(`${API}/v3/delivery/rate`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_ref: orderRef, rating, comment, role: "client" }),
    });
    setDone(true);
  };
  if (done) return <div style={{ ...cardStyle, marginTop: 10, textAlign: "center", color: "#22C55E" }}>Merci pour ta note !</div>;
  return (
    <div style={{ ...cardStyle, marginTop: 10 }}>
      <div style={{ fontSize: 13, color: "#E8ECF7", marginBottom: 8 }}>⭐ Évaluer le livreur</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`rate-star-${n}`} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: rating >= n ? "#FBBF24" : "#475569" }}>★</button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (optionnel)" rows={2} style={{ ...inputStyle, marginBottom: 10, resize: "vertical" }} />
      <button onClick={submit} disabled={!rating} data-testid="rate-submit" style={{ ...primaryBtnStyle, width: "100%", opacity: rating ? 1 : 0.5 }}>Envoyer</button>
    </div>
  );
}

const STATUS_LABELS = {
  pending_match:      { l: "Recherche livreur", icon: "🔎" },
  pending_acceptance: { l: "En attente d'acceptation", icon: "⏳" },
  accepted:           { l: "Livreur en route", icon: "🚖" },
  picked_up:          { l: "Colis pris en charge", icon: "📦" },
  delivering:         { l: "En livraison", icon: "🛵" },
  delivered:          { l: "Livré", icon: "✅" },
  cancelled:          { l: "Annulé", icon: "🚫" },
  failed:             { l: "Échec", icon: "❌" },
};

const pageStyle = { minHeight: "100vh", background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", fontFamily: "system-ui, sans-serif" };
const cardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: 14 };
const ctaAltStyle = { display: "inline-block", marginTop: 16, color: "#E8ECF7", textDecoration: "none", fontSize: 13, padding: "10px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)" };
const shareBtnStyle = { display: "inline-block", background: "#25D366", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 13, padding: "10px 22px", borderRadius: 10, boxShadow: "0 4px 14px rgba(37,211,102,0.4)" };
const replayBtnStyle = { display: "block", textAlign: "center", marginTop: 14, background: "linear-gradient(135deg,#7C3AED,#A78BFA)", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 14, padding: "12px 22px", borderRadius: 10, boxShadow: "0 4px 14px rgba(124,58,237,0.4)" };
const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 12, boxSizing: "border-box", fontFamily: "inherit" };
const primaryBtnStyle = { background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" };

function statusBadgeBig(status) {
  const colors = {
    accepted: "#60A5FA", picked_up: "#A78BFA", delivering: "#A78BFA",
    delivered: "#22C55E", cancelled: "#94A3B8", failed: "#F87171",
  };
  const c = colors[status] || "#FBBF24";
  return {
    background: "rgba(255,255,255,0.04)",
    border: `1.5px solid ${c}`,
    color: c,
    borderRadius: 12,
    padding: "12px 16px",
    fontSize: 15, fontWeight: 700,
    textAlign: "center",
  };
}
